import { skipToken } from '@apollo/client/react';
import { useState } from 'react';

import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import {
  CURRENT_CONSENT_MARKETING_EMAIL_UUID,
  CURRENT_CONSENT_PRIVACY_UUID,
  CURRENT_CONSENT_TERMS_UUID,
} from '@config/constants';
import { getConsents } from '@graphql/queries/rutilus/Me';
import { reportMykissClientError } from '@lib/errors/client';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { isNewUser } from '@domain/user/isNewUser';

import { useGiveConsent } from './useGiveConsent';
import { useUserInfo } from '@domain/user/useUserInfo';

export enum ConsentStatus {
  Accepted = 'ACCEPTED',
  UpdateRequired = 'UPDATE_REQUIRED',
  NotAccepted = 'NOT_ACCEPTED',
  NotLoggedIn = 'NOT_LOGGED_IN',
}

export const useConsent = (): {
  consentStatus: ConsentStatus;
  acceptConsent: () => Promise<void>;
} => {
  const { isLoggedIn, user: currentUser } = useUserInfo();
  const [updateConsent] = useGiveConsent();
  const [isUpdatingConsent, setIsUpdatingConsent] = useState(false);
  const { data, loading, refetch } = useRutilusQuery(
    getConsents,
    !(isLoggedIn && currentUser) ? skipToken : {},
  );

  const acceptConsent = async (): Promise<void> => {
    if (!isUpdatingConsent) {
      setIsUpdatingConsent(true);

      try {
        await Promise.all([
          updateConsent(CURRENT_CONSENT_PRIVACY_UUID),
          updateConsent(CURRENT_CONSENT_TERMS_UUID),
        ]);
        await refetch();
      } catch (error) {
        reportMykissClientError(error);
      }
      setIsUpdatingConsent(false);
    }
  };

  if (!isLoggedIn || loading || !data) {
    return { acceptConsent, consentStatus: ConsentStatus.NotLoggedIn };
  }

  const consents = getNodesFromConnection(data.me?.consents).filter(
    consent => consent.agreementUid !== CURRENT_CONSENT_MARKETING_EMAIL_UUID,
  );
  const privacy = consents.find(consent => consent.agreementUid === CURRENT_CONSENT_PRIVACY_UUID);
  const terms = consents.find(consent => consent.agreementUid === CURRENT_CONSENT_TERMS_UUID);

  if (privacy && privacy.accepted && terms?.accepted) {
    return { acceptConsent, consentStatus: ConsentStatus.Accepted };
  }
  if (consents.length === 0 || (privacy && terms)) {
    if (currentUser?.createdAt && isNewUser(currentUser.createdAt)) {
      acceptConsent().catch(reportMykissClientError);
      return { acceptConsent, consentStatus: ConsentStatus.NotLoggedIn };
    }
    return { acceptConsent, consentStatus: ConsentStatus.NotAccepted };
  }
  return { acceptConsent, consentStatus: ConsentStatus.UpdateRequired };
};
